import { makeStyles } from '@mui/styles';
import Loader from 'apollo-react/components/Loader';
import Modal from 'apollo-react/components/Modal';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ImportInfo } from './ImportInfo';
import { StatusTable } from './StatusTable';
import { UploadImportFile } from './UploadImportFile';
import {
  clearImportEntityStore,
  downloadBulkImportFile,
  initiateImportProcess,
  initiateMasterSetupImport,
  initiateSubmissionCodeImport,
  setImportModalOpen,
} from '../../../actions/BulkImportActions';
import {
  BULK_IMPORT_APIS,
  ENTITY_NAMES,
  IMPORT_STEPS,
  STEPS_DESCRIPTION,
} from '../../../constants/MasterSetupConstants';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getImportEntityProp, getIsImportModalOpen } from '../../../selectors/BulkImportSelectors';
import ButtonLoader from '../../general/Login/LoginLoader';
import { StepProgress } from '../../StepProgress';

const useStyles = makeStyles({
  modal: { minWidth: '60%' },
  downloadButton: { width: 155 },
});

const MasterSetupImportModal = ({ entityName, entityViewName, isWorkflowMasterSetup }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const translateString = useTranslation();
  const { pathname, search } = useLocation();

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDownloadLoading, setDownloadLoading] = useState(false);
  const [buttonList, setButtonList] = useState([
    {
      label: translateString('Cancel'),
      'ui-ta': 'MasterSetupImportModal-CancelButton',
    },
    {
      label: translateString('Import'),
      'ui-ta': 'MasterSetupImportModal-ImportButton',
    },
  ]);

  const isImportModalOpen = useSelector(getIsImportModalOpen);
  const importStep = useSelector(getImportEntityProp(entityName, 'importStep'));
  const importStatuses = useSelector(getImportEntityProp(entityName, 'importStatuses'));
  const isLoading = useSelector(getImportEntityProp(entityName, 'isLoading'));
  const hasImportError = useSelector(getImportEntityProp(entityName, 'hasImportError'));
  const importKey = useSelector(getImportEntityProp(entityName, 'importKey'));
  const isSubmissionCode = entityName === ENTITY_NAMES.SUBMISSION_CODE;

  const importProps = {
    entityName,
    entityViewName,
    link: `${pathname}${search}`,
  };

  const downloadImportResult = async () => {
    setDownloadLoading(true);
    await dispatch(
      downloadBulkImportFile({
        downloadType: BULK_IMPORT_APIS.DOWNLOAD_RESULT,
        isWorkflowMasterSetup,
        apiProps: { importKey },
      })
    );
    setDownloadLoading(false);
  };

  const handleImportClick = async () => {
    if (isSubmissionCode) {
      await dispatch(initiateSubmissionCodeImport(selectedFiles[0], importProps));
    } else {
      await dispatch(
        initiateMasterSetupImport(selectedFiles[0], isWorkflowMasterSetup, importProps)
      );
    }
  };

  const startNewImport = () => {
    setSelectedFiles([]);
    dispatch(clearImportEntityStore(entityName));
  };

  const cancelBtnProps = {
    label: translateString('Cancel'),
    'ui-ta': 'MasterSetupImportModal-CancelButton',
    disabled: isLoading,
  };

  const startNewImportBtnPops = {
    label: translateString('Start New Import'),
    onClick: startNewImport,
    'ui-ta': 'MasterSetupImportModal-StartNewImportButton',
  };

  useEffect(() => {
    if (!importStep) {
      dispatch(initiateImportProcess(entityName));
    }

    return () => {
      setSelectedFiles([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (hasImportError) {
      setButtonList([cancelBtnProps, startNewImportBtnPops]);
    } else if (importStep === IMPORT_STEPS.UPLOAD_COMPLETED && isSubmissionCode) {
      setButtonList([cancelBtnProps, startNewImportBtnPops]);
    } else if (importStep < IMPORT_STEPS.UPLOAD_COMPLETED) {
      setButtonList([
        cancelBtnProps,
        {
          label: translateString('Import'),
          onClick: handleImportClick,
          disabled: !selectedFiles.length || isLoading,
          'ui-ta': 'MasterSetupImportModal-ImportButton',
        },
      ]);
    } else if (importStep === IMPORT_STEPS.UPLOAD_COMPLETED) {
      setButtonList([
        cancelBtnProps,
        {
          label: isDownloadLoading ? (
            <ButtonLoader size={26} />
          ) : (
            translateString('Download Result')
          ),
          onClick: downloadImportResult,
          disabled: isDownloadLoading,
          variant: 'primary',
          'ui-ta': 'MasterSetupImportModal-DownloadResultButton',
          className: classes.downloadButton,
        },
        startNewImportBtnPops,
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importStep, isLoading, selectedFiles.length, isDownloadLoading]);

  const showImportInfo = importStep === IMPORT_STEPS.UPLOAD_COMPLETED || hasImportError;
  const showStatusTable = importStep > IMPORT_STEPS.UPLOAD_FILE && !isSubmissionCode;
  const showFileUpload = !isLoading && importStep < IMPORT_STEPS.START_IMPORT;

  return (
    <Modal
      open={isImportModalOpen}
      style={{ zIndex: 3003 }}
      variant="default"
      onClose={() => dispatch(setImportModalOpen(false))}
      title={translateString('lblBulkUpload')}
      buttonProps={buttonList}
      className={classes.modal}
      disableBackdropClick
      disableEscapeKeyDown
    >
      <StepProgress activeStep={importStep} steps={STEPS_DESCRIPTION} hasError={hasImportError} />
      {showFileUpload && (
        <UploadImportFile
          entityName={entityName}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
        />
      )}
      {isLoading && (
        <div style={{ position: 'relative', height: 135 }}>
          <Loader isInner />
        </div>
      )}
      {showImportInfo && <ImportInfo importStatuses={importStatuses} entityName={entityName} />}
      {showStatusTable && <StatusTable rows={importStatuses} />}
    </Modal>
  );
};

export default MasterSetupImportModal;
